import useMarkProposalAsSent from '@/api/mutations/proposals/useMarkProposalAsSent';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import { useToastContext } from '@payaca/ambrosia';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  buildProposalLifecycleActions,
  type ProposalLifecycleActionKey,
} from './lifecycleActions';

/** The action whose confirmation is open, if any. */
export type ProposalConfirmation = Exclude<
  ProposalLifecycleActionKey,
  'markAsSent'
>;

export type ProposalLifecycleMenuAction = {
  /**
   * Which action this is, for a surface that orders the actions itself. Named
   * `actionKey` rather than `key` so it survives being spread onto a component.
   */
  actionKey: ProposalLifecycleActionKey;
  label: string;
  onClick: () => void;
  disabled: boolean;
  disabledReasons: string[];
};

/**
 * The Proposal's lifecycle actions for the send panel's dropdown, and the open
 * state of the confirmations they lead to. Render those confirmations with
 * `ProposalLifecycleModals`.
 */
export const useProposalLifecycleActions = (params: {
  /** Public id of the Proposal — the route param. */
  proposalId: string;
  /** Public id of the parent Project — the route param. */
  projectId: string;
}): {
  actions: ProposalLifecycleMenuAction[];
  isMarkingAsSent: boolean;
  openConfirmation: ProposalConfirmation | null;
  closeConfirmation: () => void;
} => {
  const { proposalId, projectId } = params;

  const history = useHistory();
  const { pushSuccess } = useToastContext();
  const { proposal } = useGetProposal(proposalId);

  const [openConfirmation, setOpenConfirmation] =
    useState<ProposalConfirmation | null>(null);
  const closeConfirmation = () => setOpenConfirmation(null);

  const markProposalAsSent = useMarkProposalAsSent({ proposalId, projectId });

  const handleMarkAsSent = () =>
    markProposalAsSent.mutate(
      { id: proposalId },
      {
        onSuccess: () => {
          pushSuccess('Proposal marked as sent.');
          history.push(`/projects/${projectId}/proposals`);
        },
      }
    );

  const actionHandlers: Record<ProposalLifecycleActionKey, () => void> = {
    markAsSent: handleMarkAsSent,
    markAsAccepted: () => setOpenConfirmation('markAsAccepted'),
    markAsDeclined: () => setOpenConfirmation('markAsDeclined'),
    removeAccepted: () => setOpenConfirmation('removeAccepted'),
    replace: () => setOpenConfirmation('replace'),
  };

  const actions = buildProposalLifecycleActions({ proposal }).map((action) => ({
    actionKey: action.key,
    label: action.label,
    onClick: actionHandlers[action.key],
    disabled: action.disabled,
    // MenuItemCore renders reasons only when disabled.
    disabledReasons: action.disabledReasons,
  }));

  return {
    actions,
    isMarkingAsSent: markProposalAsSent.isPending,
    openConfirmation,
    closeConfirmation,
  };
};
